import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext.jsx';
import { Sparkles, Heart, Star, User, Lock, ArrowRight, Delete, RotateCcw } from 'lucide-react';
import axios from 'axios';

function KioskLogin() {
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [floatingElements, setFloatingElements] = useState([]);
  const { login, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // API configuration
  const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";

  // ✅ CRITICAL FIX: Clear any admin session on mount
  useEffect(() => {
    // Clear admin tokens to prevent cross-contamination
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('authToken'); // Legacy key
    localStorage.removeItem('user'); // Legacy key
    
    console.log('Kiosk login mounted - admin session cleared');
  }, []);

  // Fetch staff list (Cashiers, Customer Service Assistants, AND Managers)
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/staff?status=active`, {
          timeout: 10000
        });
        const kioskStaff = response.data.filter(staff =>
          ['Cashier', 'Customer Service Assistant', 'Manager'].includes(staff.position)
        );
        setStaffList(kioskStaff);
      } catch (err) {
        console.error('Error fetching staff:', err);
        setError('Failed to load staff list. Please check your internet connection.');
      }
    };
    fetchStaff();
  }, [API_BASE_URL]);

  useEffect(() => {
    // Create floating elements for background animation
    const elements = Array.from({ length: 8 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 3 + Math.random() * 2
    }));
    setFloatingElements(elements);
  }, []);

  const handlePinClick = (value) => {
    if (value === 'clear') {
      setPin('');
    } else if (value === 'backspace') {
      setPin((prev) => prev.slice(0, -1));
    } else {
      setPin((prev) => prev + value);
    }
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleLogin = async () => {
    if (!selectedStaff || !pin) {
      setError('Please select a staff member and enter a PIN');
      return;
    }
    setError('');
    setLoading(true);
    try {
      console.log('Calling login with:', { staffId: selectedStaff, pin });
      const response = await login(selectedStaff, pin, true);
      console.log('Login response:', response);
      
      // ✅ CRITICAL FIX: Store in kiosk-specific keys
      localStorage.setItem('kioskAuthToken', response.token);
      localStorage.setItem('kioskUser', JSON.stringify(response.user));
      
      navigate('/kiosk/pos/salon', { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Invalid staff selection or PIN. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const selectedStaffName = staffList.find(staff => staff._id === selectedStaff);

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600">
      {/* Floating Background Elements */}
      <div className="absolute inset-0">
        {floatingElements.map((element) => (
          <div
            key={element.id}
            className="absolute w-4 h-4 bg-white/10 rounded-full"
            style={{
              left: `${element.x}%`,
              top: `${element.y}%`,
              animation: `float ${element.duration}s ease-in-out infinite ${element.delay}s alternate`
            }}
          />
        ))}
      </div>

      {/* Decorative Icons */}
      <div className="absolute top-20 left-10 text-white/20">
        <Sparkles className="w-8 h-8" />
      </div>
      <div className="absolute top-40 right-20 text-white/20">
        <Heart className="w-6 h-6" />
      </div>
      <div className="absolute bottom-20 left-20 text-white/20">
        <Star className="w-5 h-5" />
      </div>
      <div className="absolute top-60 right-10 text-white/20">
        <Sparkles className="w-4 h-4" />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-lg">
          {/* Header */}
          <div className="text-center mb-8">
            <img
              src={`${API_BASE_URL}/logo.png`}
              alt="Ellas Place Logo"
              className="mx-auto w-20 h-20 rounded-full mb-4 shadow-lg"
              onError={(e) => {
                e.target.src = "/fallback-logo.png";
              }}
            />
            <h1 className="text-3xl font-bold text-white mb-2">Ellas Place</h1>
            <p className="text-white/80 text-sm">Kiosk Point of Sale</p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-300 to-indigo-300 rounded-full mx-auto mt-3"></div>
          </div>

          <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-8 border border-white/20">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Staff Login</h2>
              <p className="text-gray-600 text-sm">Select your name and enter your PIN</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-600 text-sm text-center font-medium">{error}</p>
              </div>
            )}

            {/* Staff Selection */}
            <div className="mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <User className="w-5 h-5 text-blue-500" />
                </div>
                <select
                  value={selectedStaff}
                  onChange={(e) => {
                    setSelectedStaff(e.target.value);
                    if (error) setError('');
                  }}
                  className="block w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 backdrop-blur-sm text-gray-800 font-medium transition-all duration-300"
                  disabled={loading}
                >
                  <option value="" disabled className="text-gray-500">Select Staff Member</option>
                  {staffList.map((staff) => (
                    <option key={staff._id} value={staff._id} className="text-gray-800">
                      {staff.firstName} {staff.lastName} - {staff.position}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* PIN Display */}
            <div className="mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-blue-500" />
                </div>
                <div className="block w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl bg-white/80 backdrop-blur-sm min-h-[3.5rem] flex items-center">
                  <div className="flex space-x-2">
                    {pin.split('').map((_, index) => (
                      <div
                        key={index}
                        className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"
                      />
                    ))}
                    {pin.length === 0 && (
                      <span className="text-gray-400 text-base">Enter your PIN</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* PIN Keypad */}
            <div className="mb-6">
              <div className="grid grid-cols-3 gap-3">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((number) => (
                  <button
                    key={number}
                    onClick={() => handlePinClick(number)}
                    className="h-14 bg-gradient-to-b from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border border-blue-200 rounded-xl font-semibold text-blue-800 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-sm"
                    disabled={loading}
                  >
                    {number}
                  </button>
                ))}
                
                {/* Clear button */}
                <button
                  onClick={() => handlePinClick('clear')}
                  className="h-14 bg-gradient-to-b from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border border-gray-200 rounded-xl font-medium text-gray-700 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-sm flex items-center justify-center"
                  disabled={loading}
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                
                {/* Zero */}
                <button
                  onClick={() => handlePinClick('0')}
                  className="h-14 bg-gradient-to-b from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border border-blue-200 rounded-xl font-semibold text-blue-800 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-sm"
                  disabled={loading}
                >
                  0
                </button>
                
                {/* Backspace button */}
                <button
                  onClick={() => handlePinClick('backspace')}
                  className="h-14 bg-gradient-to-b from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border border-gray-200 rounded-xl font-medium text-gray-700 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-sm flex items-center justify-center"
                  disabled={loading}
                >
                  <Delete className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button
              onClick={handleLogin}
              disabled={loading || !selectedStaff || !pin}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center group shadow-lg"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  {selectedStaffName ? `Login as ${selectedStaffName.firstName}` : 'Login to POS'}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </>
              )}
            </button>

            {/* Status Display */}
            {selectedStaff && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-center justify-center text-blue-700 text-sm">
                  <User className="w-4 h-4 mr-2" />
                  <span className="font-medium">
                    Selected: {selectedStaffName?.firstName} {selectedStaffName?.lastName} ({selectedStaffName?.position})
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-white/60 text-xs">
              © 2025 Ellas Place. All rights reserved. Powered by{" "}
              <a 
                href="https://opsuite.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/80 hover:text-white font-medium underline"
              >
                Opsuite.io
              </a>{" "}
              <a 
                href="https://wa.me/2349052463435" 
                className="text-white/60 hover:text-white/80"
              >
                09052463435
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* CSS Animation */}
      <style>
        {`
          @keyframes float {
            0% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-10px) rotate(180deg); }
            100% { transform: translateY(0px) rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

export default KioskLogin;